import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { type ModuleTier, isModuleAccessible, getModulesByTier, ADDON_DEFINITIONS } from '@/lib/featureFlags';

/**
 * Hook para obter o tier do tenant atual e status dos addons.
 * 
 * Consulta emp_feature_flags para derivar:
 * - Tier do cluster (basico/profissional/enterprise)
 * - Addons ativos (addon_erp, addon_bim)
 * 
 * Fallback → 'enterprise' (acesso total durante fase preparatória)
 */
export function useTenantTier() {
  const { user, role, gestorClusterIds } = useAuth();

  // Query tier
  const { data: tenantTier = 'enterprise' as ModuleTier, isLoading: tierLoading } = useQuery({
    queryKey: ['tenant-tier', user?.id],
    queryFn: async (): Promise<ModuleTier> => {
      const { data, error } = await (supabase as any)
        .from('emp_feature_flags')
        .select('*')
        .eq('modulo', 'empreendimentos')
        .eq('tipo_modulo', 'tier')
        .eq('ativo', true)
        .limit(1);

      if (error || !data || data.length === 0) {
        return 'enterprise';
      }

      const row = data[0] as any;
      const meta = row?.metadata || row?.config || {};
      if (meta?.tenant_tier && ['basico', 'profissional', 'enterprise'].includes(meta.tenant_tier)) {
        return meta.tenant_tier as ModuleTier;
      }

      return 'enterprise';
    },
    enabled: !!user && !!role,
    staleTime: 10 * 60 * 1000,
  });

  // Query active addons
  const { data: activeAddons = [] as string[], isLoading: addonsLoading } = useQuery({
    queryKey: ['tenant-addons', user?.id, role],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await (supabase as any)
        .from('emp_feature_flags')
        .select('modulo, cluster_id')
        .eq('tipo_modulo', 'addon')
        .eq('ativo', true);

      if (error || !data) return [];

      // Filter by cluster access
      const rows = data as Array<{ modulo: string; cluster_id: string }>;
      
      if (role === 'admin' || role === 'coadmin') {
        // Admin sees all active addons
        return [...new Set(rows.map(r => r.modulo))];
      }

      if (role === 'gestor' && gestorClusterIds.length > 0) {
        return [...new Set(
          rows
            .filter(r => gestorClusterIds.includes(r.cluster_id))
            .map(r => r.modulo)
        )];
      }

      // Other roles: check their cluster
      return [...new Set(rows.map(r => r.modulo))];
    },
    enabled: !!user && !!role,
    staleTime: 10 * 60 * 1000,
  });

  const isLoading = tierLoading || addonsLoading;

  const checkModuleAccess = useMemo(() => {
    return (moduleId: string) => isModuleAccessible(moduleId, tenantTier);
  }, [tenantTier]);

  /**
   * Verifica se um addon específico está ativo para o tenant atual.
   */
  const hasAddon = useMemo(() => {
    return (addonId: string) => activeAddons.includes(addonId);
  }, [activeAddons]);

  const moduleReport = useMemo(() => getModulesByTier(), []);

  return {
    tenantTier,
    isLoading,
    checkModuleAccess,
    hasAddon,
    activeAddons,
    moduleReport,
  };
}
